import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DialogTitle } from '@headlessui/react';
import DOMPurify from 'dompurify';
import {
  IconArrowUpRight,
  IconChevronLeft,
  IconChevronRight,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { listRecipeResults, type RecipeResult } from '../../services/recipeAutomationService';
import { Modal } from '../ui/Modal';
import { TONE_CLASSES, toneAt } from './analytics/chartTheme';
import './recipe-content.css';

/** Match the response selector's attribution, including its stable campaign number. */
function respondentLabel(result: RecipeResult): string {
  return result.responseNumber == null
    ? result.respondentName
    : `${result.respondentName} #${result.responseNumber}`;
}

/** A short text preview derived from sanitized content, never executable HTML. */
export function recipeResultPreview(content: string): string {
  const document = DOMPurify.sanitize(content, {
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'li'],
    ALLOWED_ATTR: [],
  });
  document.querySelectorAll('p,h1,h2,h3,li').forEach((block) => block.append(' '));
  return (document.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 220);
}
/** Readable result markup uses the same restricted document vocabulary as recipes. */
function safeContent(content: string): string {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'article',
      'section',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'hr',
      'span',
      'div',
      'ul',
      'ol',
      'li',
      'strong',
      'em',
      'b',
      'i',
      'u',
      'a',
      'blockquote',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
    ],
    ALLOWED_ATTR: ['href', 'title', 'class'],
  });
}
/** Keep generated results in sync while fencing refreshes to the requested scope. */
export function useRecipeResults(sessionId: string, responseId?: string, enabled = true, localContent?: Record<string, string | null>) {
  const { supabase } = useSupabase();
  const scope = `${sessionId}:${responseId ?? ''}`;
  const currentLocal = useRef(localContent);
  useEffect(() => { currentLocal.current = localContent; }, [localContent]);
  const [state, setState] = useState<{ scope: string; cards: RecipeResult[]; error: boolean; localAtRead?: typeof localContent }>({
    scope: '',
    cards: [],
    error: false,
  });
  const [limit, setLimit] = useState(20);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    setLimit(20);
  }, [scope]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    /** Poll serially so a slow request cannot overwrite a newer refresh. */
    const refresh = async () => {
      const localAtRead = currentLocal.current;
      try {
        const cards = await listRecipeResults(supabase, sessionId, responseId, limit);
        if (!cancelled) setState({ scope, cards, error: false, localAtRead });
      } catch (error) {
        if (!cancelled) {
          setState((previous) => ({
            scope,
            cards: previous.scope === scope ? previous.cards : [],
            error: true,
            localAtRead: previous.scope === scope ? previous.localAtRead : undefined,
          }));
          captureException(
            error instanceof Error ? error : new Error(String(error)),
            'RecipeResults.load',
            { sessionId, responseId },
          );
        }
      } finally {
        if (!cancelled)
          timer = setTimeout(() => {
            if (document.hidden)
              timer = setTimeout(() => {
                void refresh();
              }, 10_000);
            else void refresh();
          }, 10_000);
      }
    };
    void refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [supabase, scope, sessionId, responseId, limit, revision, enabled]);
  const cards = state.scope === scope
    ? state.cards.filter(card => card.status === 'complete' && Boolean(card.content?.trim()))
    : [];
  const error = state.scope === scope && state.error;
  // A local generation may finish after this read began. Keep its HTML until a subsequent read.
  const contentByRecipe = { ...localContent };
  for (const card of cards) {
    const local = localContent?.[card.recipeId];
    if (!local || local === state.localAtRead?.[card.recipeId]) contentByRecipe[card.recipeId] = card.content;
  }
  return { cards, contentByRecipe, error, limit, showMore: () => setLimit(value => value + 20), retry: () => setRevision(value => value + 1) };
}

/** Recipe result cards shared by the campaign and single-response overviews. */
export function RecipeResults({
  sessionId,
  responseId,
}: {
  sessionId: string;
  responseId?: string;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
    setSelectedId(null);
  }, [sessionId, responseId]);
  const { cards, error, limit, showMore, retry } = useRecipeResults(sessionId, responseId);
  const selected = cards.find((card) => card.id === selectedId && card.content);
  const pageSize = responseId ? 1 : 2;
  const lastPage = Math.max(0, Math.ceil(cards.length / pageSize) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleCards = cards.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  if (!cards.length) return null;
  const pages = lastPage + 1;
  const arrow = 'rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring';
  return (
    <section aria-label="Recipe results" className="relative flex min-w-0 flex-col gap-4">
      {(!responseId || pages > 1) && <header className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">Recipe results</h2>
        {pages > 1 && (
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="flex gap-1.5">
              {Array.from({ length: pages }, (_, index) => (
                <span key={index} className={`h-1.5 w-1.5 rounded-full ${index === currentPage ? 'bg-accent-soft' : 'bg-border'}`} />
              ))}
            </span>
            <span className="sr-only" aria-live="polite">{`${currentPage + 1} / ${pages}`}</span>
            <button type="button" aria-label="Previous results" className={arrow}
              onClick={() => setPage((currentPage - 1 + pages) % pages)}>
              <IconChevronLeft aria-hidden="true" className="h-4 w-4" />
            </button>
            <button type="button" aria-label="Next results" className={arrow}
              onClick={() => setPage((currentPage + 1) % pages)}>
              <IconChevronRight aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        )}
      </header>}
      {error && (
        <div role="alert" className="flex items-center gap-3 text-sm text-danger-soft">
          Could not load recipe results
          <button
            type="button"
            onClick={retry}
            className="underline cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}
      <div className={`grid flex-1 grid-cols-1 gap-4 ${visibleCards.length > 1 ? 'md:grid-cols-2' : ''}`}>
        {visibleCards.map((card, index) => (
          <button
            type="button"
            key={card.id}
            aria-label={`Open ${card.title} for ${respondentLabel(card)}`}
            onClick={() => setSelectedId(card.id)}
            className={`${TONE_CLASSES[toneAt(currentPage * pageSize + index)]} flex flex-col gap-3 items-stretch min-w-0 rounded-[20px] p-6 text-left cursor-pointer transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-ring`}
          >
            <div className="flex items-center gap-2">
              <IconSparkles aria-hidden="true" className="h-4 w-4 shrink-0 text-[var(--chart-mark)]" />
              <h3 className="text-base font-semibold text-foreground">{card.title}</h3>
              <IconArrowUpRight aria-hidden="true" className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
            <p className="text-[15px] font-medium leading-relaxed text-foreground line-clamp-3">
              {recipeResultPreview(card.content ?? '')}
            </p>
            {!responseId && <p className="mt-auto text-sm text-muted-foreground">{respondentLabel(card)}</p>}
          </button>
        ))}
      </div>
      {currentPage === lastPage && cards.length >= limit && (
        <button
          type="button"
          onClick={showMore}
          className="text-sm text-muted-foreground hover:text-foreground underline cursor-pointer"
        >
          Show more
        </button>
      )}
      <RecipeResultReader selected={selected} sessionId={sessionId} onClose={() => setSelectedId(null)} />
    </section>
  );
}

/** Open an existing result even when its source recipe is no longer in the catalog. */
export function RecipeResultReader({ selected, sessionId, onClose }: {
  selected?: RecipeResult;
  sessionId: string;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!selected}
      onClose={onClose}
      maxWidth="max-w-3xl"
    >
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <DialogTitle className="text-xl font-bold text-foreground">{selected?.title}</DialogTitle>
          {selected && <Link
            to={`/admin/interviews/${sessionId}/responses/${selected.responseId}`}
            onClick={onClose}
            className="inline-block text-sm text-muted-foreground no-underline hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
          >
            {respondentLabel(selected)}
          </Link>}
        </div>
        <button
          type="button"
          aria-label="Close result"
          onClick={onClose}
          className="-mt-1 -mr-2 shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring cursor-pointer"
        >
          <IconX size={20} />
        </button>
      </header>
      <div
        className="recipe-content max-h-[65vh] overflow-auto scrollbar-dark text-sm text-foreground leading-relaxed [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:mb-5 [&_h2]:my-4 [&_h3]:my-3 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-volt [&_blockquote]:pl-4"
        dangerouslySetInnerHTML={{ __html: safeContent(selected?.content ?? '') }}
      />
    </Modal>
  );
}
